// Per-surface copy namespaces (BUILD 1 contract C-14).
//
// Each `ui/i18n/ns/<name>.ts` holds one surface's copy in all 24 app
// languages and has exactly one owner package. A namespace file:
//   - default-exports a `NamespaceCatalog<Key>` whose `en` entry defines the keys;
//   - uses `{name}` placeholders, filled with `formatCopy(template, values)`;
//   - supplies translated copy for every supported language.
// test/chat-i18n-namespaces.mjs enforces languages, key parity, placeholder
// parity, trimmed values and translated entries. Unfinished catalogs fail CI.
//
// Server responses stay codes; copy lives only here.
import { resolveCatalogLocale, useAppCatalogLocale } from '@/i18n/context';
import type { EuAppLanguage } from '@/i18n/eu-locales';

export { interpolateCopy as formatCopy } from '@/i18n/context';

export type NamespaceCatalog<K extends string> = Record<EuAppLanguage, Record<K, string>>;
export type NamespaceKey<C> = C extends NamespaceCatalog<infer K> ? K : never;

/** The catalog entry for a resolved catalog locale, falling back to English. */
export function namespaceCopyFor<K extends string>(
  catalog: NamespaceCatalog<K>,
  locale: string | null | undefined,
): Readonly<Record<K, string>> {
  return catalog[resolveCatalogLocale(locale)];
}

/** The namespace's copy in the app's interface language (English fallback). */
export function useNamespaceCopy<K extends string>(
  catalog: NamespaceCatalog<K>,
): Readonly<Record<K, string>> {
  return namespaceCopyFor(catalog, useAppCatalogLocale());
}
